import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { logger } from "@/lib/logger";

type Business = Tables<"businesses">;

interface BusinessContextType {
  business: Business | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBusiness = useCallback(async () => {
    if (!user) {
      setBusiness(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("businesses")
        .select("*")
        .eq("owner_user_id", user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setBusiness(data);
    } catch (err) {
      logger.error("Business fetch error", err instanceof Error ? err : new Error(String(err)));
      setError(err instanceof Error ? err.message : "Failed to fetch business");
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchBusiness();
  }, [fetchBusiness]);

  return (
    <BusinessContext.Provider value={{ business, loading, error, refetch: fetchBusiness }}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error("useBusiness must be used within a BusinessProvider");
  }
  return context;
}
